import type {
  FavoriteTreeItem,
  LocalProjectFolderOpen,
} from '../../../generated';
import type { TypePolicies } from '../../../utils/types/TypePolicies';
import {
  favoritedAssets,
  favoritedProjects,
  hashtags,
  openedProjectFolders,
} from './state';

/**
 * we use Apollo type policies to read from (and initialize) local state objects
 * as if they were on the remote.
 */
export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      /**
       * Apollo will execute this function every time a `isProjectFavorited` query runs
       * @returns the result of the query
       */
      isProjectFavorited: (_existing, { variables }) => {
        /** these are the variables that are passed into the query */
        const { userId, projectId } = variables;

        /**
         * get the array of favorited project IDs for the user
         * initialize with an empty array if one doesn't exist for this user yet
         * */
        const userFavoritedProjects = favoritedProjects.get(userId, []);

        /** return whether the favorited projects include the one being queried */
        return userFavoritedProjects.includes(projectId);
      },

      /**
       * Apollo will execute this function every time a `isAssetFavorited` query runs
       * @returns the result (boolean) of the query
       */
      isAssetFavorited: (_existing, { variables }) => {
        const { userId, projectId, assetId } = variables;

        // avoid initializing on get() with undefined.
        if (!userId) return false;

        const userFavoritedAssets = favoritedAssets.get(userId, []);

        const includesAsset = (asset: FavoriteTreeItem) => {
          return asset.projectId === projectId && asset.id === assetId;
        };

        return userFavoritedAssets.some(includesAsset);
      },

      /**
       * Apollo will execute this function every time a `assetFavorites` query runs
       * @returns the result of the query list: {result: [FavoriteTreeBranch]}
       */
      assetFavorites: (_existing, { variables }) => {
        const { userId, projectId } = variables;
        const favorites = () => {
          // avoid initializing on get() with undefined.
          if (!userId && !projectId) return [];

          const userFavoritedAssets = favoritedAssets.get(userId, []);
          const includeProject = (asset: FavoriteTreeItem) => {
            return asset.projectId === projectId;
          };
          return userFavoritedAssets.filter(includeProject);
        };

        /*
         * for @client uses. We mimic the response from massdriver
         * with the inclusion of __typename,
         * otherwise the schema in operations will not return any children!
         */
        return {
          result: {
            __typename: 'FavoriteTreeBranch',
            id: 'root-favorites',
            children: favorites().map((child: FavoriteTreeItem) => {
              return { ...child, __typename: 'FavoriteTreeItem' };
            }),
          },
        };
      },

      /**
       * @returns the what project folders are "open" or closed.
       */
      projectFoldersOpen: (_existing, { variables }) => {
        const { userId, projectId } = variables;
        const opened = () => {
          // avoid initializing on get() with undefined.
          if (!userId && !projectId) return [];

          const usersOpenedProjectFolders = openedProjectFolders.get(
            userId,
            []
          );
          const includeProject = (asset: LocalProjectFolderOpen) => {
            return asset.projectId === projectId;
          };
          return usersOpenedProjectFolders.filter(includeProject);
        };

        return {
          result: opened(),
        };
      },

      availHashtags: (_existing, { variables }) => {
        /** these are the variables used to define which type of hashtags can be shown to this user when not local */
        const { userId: __userId, projectId } = variables;
        const hashtagList = hashtags.get(projectId, []);

        return hashtagList;
      },
    },
  },
};
